import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import { formatMoney, formatMoment, toNumber } from '../ui/format'
import {
  Banner, Button, Empty, ErrorBanner, Field, Panel, Spinner, TextInput,
} from '@shop/ui'

type OrderLine = {
  productName: string
  variantLabel: string | null
  unitPrice: number
  qty: number
  lineTotal: number
}

type WebOrder = {
  id: string
  orderNo: string
  customerName: string
  phone: string
  fulfilment: 'PICKUP' | 'DELIVERY'
  address: string | null
  paymentMethod: 'COD' | 'CLIQ'
  cliqReference: string | null
  note: string | null
  itemsTotal: number
  deliveryFee: number | null
  status: 'NEW' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED'
  paid: boolean
  staffNote: string | null
  createdAt: string
  lines: OrderLine[]
}

type Preorder = {
  id: string
  phone: string
  device: string
  note: string | null
  status: string
  createdAt: string
}

/** طلبُ «خدمةٍ إلكترونيّة» جمعه مساعدُ الموقع (2026-10-28). */
type ServiceRequest = {
  id: string
  service: string
  name: string
  phone: string
  email: string | null
  details: string
  budget: string | null
  timeline: string | null
  createdAt: string
}

/** كلمةٌ اقترحها لاعبٌ في «حرف وتحدّي» لأنّها «غير معتمدة في القاموس» (2026-10-28). */
type WordSuggestion = {
  id: string
  category: 'name' | 'animal' | 'plant' | 'thing' | 'country' | 'city'
  word: string
  letter: string | null
  suggestedBy: string
  createdAt: string
}

/** «حرف وتحدّي» الآن — مَن على صفحة اللعبة، والغرفُ الحيّة بلا رموزها (2026-10-29). */
type GameLive = {
  connected: number
  rooms: number
  inMatch: number
  players: number
  /** «حجرة ورقة مقص» (2026-10-30) — عددٌ لا قائمة. */
  rps?: { duels: number; inMatch: number; players: number; vsComputer: number }
  /** «الشايب» (2026-10-31) — عددٌ لا قائمة. */
  shayeb?: { tables: number; inMatch: number; players: number; bots: number }
  list: {
    public: boolean
    phase: 'lobby' | 'countdown' | 'playing' | 'results' | 'final'
    round: number
    rounds: number
    maxPlayers: number
    since: string
    players: { name: string; avatar: string; guest: boolean; online: boolean; host: boolean; total: number }[]
  }[]
}

function phaseName(room: GameLive['list'][number]): string {
  switch (room.phase) {
    case 'lobby': return 'بانتظار اللاعبين'
    case 'countdown': return `تبدأ الجولة ${room.round}`
    case 'playing': return `الجولة ${room.round} من ${room.rounds}`
    case 'results': return `نتائج الجولة ${room.round}`
    case 'final': return 'انتهت المباراة'
  }
}

/** عمرُ الغرفة — «منذ ٦ د» أقرأُ من تاريخٍ كامل لشيءٍ عمرُه دقائق. */
function roomAge(since: string): string {
  const minutes = Math.floor((Date.now() - Date.parse(since)) / 60_000)

  if (minutes < 1) return 'للتوّ'
  if (minutes < 60) return `منذ ${minutes} د`

  return `منذ ${Math.floor(minutes / 60)} س ${minutes % 60} د`
}

const categoryName: Record<WordSuggestion['category'], string> = {
  name: 'اسم',
  animal: 'حيوان',
  plant: 'نبات',
  thing: 'جماد',
  country: 'دولة',
  city: 'مدينة',
}

type Options = {
  enabled: boolean
  deliveryEnabled: boolean
  deliveryFee: number | null
  deliveryArea: string | null
  cliqAlias: string | null
  cliqName: string | null
}

const statusName: Record<WebOrder['status'], string> = {
  NEW: 'جديد',
  CONFIRMED: 'مؤكَّد',
  COMPLETED: 'اكتمل',
  CANCELLED: 'أُلغي',
}

const statusTone: Record<WebOrder['status'], string> = {
  NEW: 'bg-warn/15 text-warn',
  CONFIRMED: 'bg-brand/15 text-brand',
  COMPLETED: 'bg-ok/15 text-ok',
  CANCELLED: 'bg-ink/10 text-ink-soft',
}

/** رقمٌ أردنيٌّ لواتساب — `0791234567` ⇐ `962791234567`. */
function whatsapp(phone: string, text: string): string {
  const digits = phone.replace(/\D/g, '').replace(/^00962|^962|^0/, '')

  return `https://wa.me/962${digits}?text=${encodeURIComponent(text)}`
}

/**
 * طلباتُ الموقع — <b>ما يصل من سلّة aqabamobile.com، وحجوزاتُه، وإعداداتُه</b>.
 *
 * <p>
 * ⚠ <b>الطلبُ وعدٌ لا بيعة.</b> «اكتمل» لا يُنقص المخزونَ ولا يُدخل مالًا: البيعةُ
 * تُسجَّل في نقطة البيع حين يُسلَّم الصنف، كأيّ بيعة — فيبقى جردُ الكاش والمخزون
 * صادقًا. وهذا مكتوبٌ على الشاشة لأنّه أوّلُ ما يُسأل.
 * </p>
 *
 * <p>
 * ⚠ <b>والحجوزاتُ هنا أيضًا</b> — وكانت تُجمع منذ أشهرٍ بلا شاشةٍ تُقرأ منها.
 * </p>
 */
export default function WebOrders() {
  const { can } = useAuth()
  const queries = useQueryClient()
  const [openOnly, setOpenOnly] = useState(true)

  const orders = useQuery({
    queryKey: ['web-orders', openOnly],
    queryFn: () => api<WebOrder[]>(`/api/v1/web-orders?openOnly=${openOnly}`),
    enabled: can(Perm.webOrderView),

    // طلبٌ جديدٌ يُرى خلال دقيقة بلا تحديثٍ يدويّ.
    refetchInterval: 60_000,
  })

  const preorders = useQuery({
    queryKey: ['preorders'],
    queryFn: () => api<Preorder[]>('/api/v1/preorders?openOnly=true'),
    enabled: can(Perm.preorderView),
    refetchInterval: 120_000,
  })

  const services = useQuery({
    queryKey: ['service-requests'],
    queryFn: () => api<ServiceRequest[]>('/api/v1/service-requests?openOnly=true'),
    enabled: can(Perm.preorderView),
    refetchInterval: 120_000,
  })

  const closeService = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api(`/api/v1/service-requests/${id}/close`, { method: 'POST', body: { status } }),
    onSuccess: () => queries.invalidateQueries({ queryKey: ['service-requests'] }),
  })

  const live = useQuery({
    queryKey: ['games-live'],
    queryFn: () => api<GameLive>('/api/v1/games/live'),
    enabled: can(Perm.webOrderView),

    // العدُّ يتغيّر كلَّ لحظة — وطلبُه ذاكرةٌ لا قاعدة.
    refetchInterval: 10_000,
  })

  const words = useQuery({
    queryKey: ['game-suggestions'],
    queryFn: () => api<WordSuggestion[]>('/api/v1/games/suggestions'),
    enabled: can(Perm.webOrderView),
    refetchInterval: 300_000,
  })

  const decideWord = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      api(`/api/v1/games/suggestions/${id}/decide`, { method: 'POST', body: { approve } }),
    onSuccess: () => queries.invalidateQueries({ queryKey: ['game-suggestions'] }),
  })

  const act = useMutation({
    mutationFn: ({ id, action, staffNote }: { id: string; action: string; staffNote?: string }) =>
      api<WebOrder>(`/api/v1/web-orders/${id}`, { method: 'POST', body: { action, staffNote } }),
    onSuccess: () => queries.invalidateQueries({ queryKey: ['web-orders'] }),
  })

  const closePreorder = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api(`/api/v1/preorders/${id}/close`, { method: 'POST', body: { status } }),
    onSuccess: () => queries.invalidateQueries({ queryKey: ['preorders'] }),
  })

  if (!can(Perm.webOrderView) && !can(Perm.preorderView)) {
    return (
      <Panel title="طلباتُ الموقع">
        <Banner tone="warn">هذه الشاشةُ تحتاج صلاحيّةَ «عرضُ طلبات الموقع».</Banner>
      </Panel>
    )
  }

  const handle = can(Perm.webOrderHandle)

  return (
    <>
      {can(Perm.webOrderView) && (
        <Panel
          title="طلباتُ الموقع"
          actions={
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!openOnly} onChange={(e) => setOpenOnly(!e.target.checked)} />
              المنتهيةُ أيضًا
            </label>
          }
        >
          <Banner tone="info">
            «اكتمل» لا يبيع ولا يُنقص المخزون — <b>سجّل البيعةَ في نقطة البيع</b> حين تسلّم الطلب، فيبقى
            جردُ الكاش صحيحًا.
          </Banner>

          <ErrorBanner error={orders.error ?? act.error} />
          {orders.isPending && <Spinner />}

          {orders.data && orders.data.length === 0 && (
            <Empty>{openOnly ? 'لا طلباتٍ مفتوحة الآن.' : 'لا طلباتَ بعد.'}</Empty>
          )}

          <ul className="mt-3 grid gap-3">
            {orders.data?.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                canHandle={handle}
                busy={act.isPending}
                onAct={(action, staffNote) => act.mutate({ id: order.id, action, staffNote })}
              />
            ))}
          </ul>
        </Panel>
      )}

      {can(Perm.preorderView) && (
        <Panel title="حجوزاتُ الأجهزة القادمة">
          <ErrorBanner error={preorders.error ?? closePreorder.error} />
          {preorders.data && preorders.data.length === 0 && <Empty>لا حجوزاتٍ مفتوحة.</Empty>}

          <ul className="grid gap-2">
            {preorders.data?.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-line px-3 py-2">
                <b>{p.device}</b>
                <a className="tabular underline" dir="ltr" href={`tel:${p.phone}`}>{p.phone}</a>
                <span className="text-xs text-ink-soft">{formatMoment(p.createdAt)}</span>
                {p.note && <span className="text-sm text-ink-soft">{p.note}</span>}

                <span className="mr-auto flex gap-2">
                  <a
                    className="rounded-md border border-line px-3 py-1 text-sm"
                    href={whatsapp(p.phone, `مرحبا، معك عال العال للموبايل بخصوص حجزك لـ${p.device}`)}
                    target="_blank"
                    rel="noopener"
                  >
                    واتساب
                  </a>
                  {can(Perm.preorderHandle) && (
                    <>
                      <Button onClick={() => closePreorder.mutate({ id: p.id, status: 'CONTACTED' })}>
                        اتّصلنا
                      </Button>
                      <Button tone="danger" onClick={() => closePreorder.mutate({ id: p.id, status: 'CANCELLED' })}>
                        إلغاء
                      </Button>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {can(Perm.preorderView) && (
        <Panel title="طلباتُ الخدمات الإلكترونيّة">
          <p className="mb-2 text-sm text-ink-soft">
            مواقع وبرامج وأنظمة — يجمعها مساعدُ صفحة «خدمات إلكترونية» من الزبون.
          </p>
          <ErrorBanner error={services.error ?? closeService.error} />
          {services.data && services.data.length === 0 && <Empty>لا طلباتِ خدماتٍ مفتوحة.</Empty>}

          <ul className="grid gap-3">
            {services.data?.map((r) => (
              <li key={r.id} className="grid gap-1 rounded-lg border border-line px-3 py-2">
                <div className="flex flex-wrap items-center gap-3">
                  <b>{r.service}</b>
                  <span>{r.name}</span>
                  <a className="tabular underline" dir="ltr" href={`tel:${r.phone}`}>{r.phone}</a>
                  {r.email && <span className="text-sm" dir="ltr">{r.email}</span>}
                  <span className="text-xs text-ink-soft">{formatMoment(r.createdAt)}</span>
                </div>
                <p className="whitespace-pre-line text-sm">{r.details}</p>
                {(r.budget || r.timeline) && (
                  <p className="text-xs text-ink-soft">
                    {r.budget && <>الميزانيّة: {r.budget} </>}
                    {r.timeline && <>· الموعد: {r.timeline}</>}
                  </p>
                )}
                <span className="flex flex-wrap gap-2">
                  <a
                    className="rounded-md border border-line px-3 py-1 text-sm"
                    href={whatsapp(r.phone, `مرحبا ${r.name}، معك عال العال للموبايل بخصوص طلبك: ${r.service}`)}
                    target="_blank"
                    rel="noopener"
                  >
                    واتساب
                  </a>
                  {can(Perm.preorderHandle) && (
                    <>
                      <Button onClick={() => closeService.mutate({ id: r.id, status: 'CONTACTED' })}>
                        اتّصلنا
                      </Button>
                      <Button tone="danger" onClick={() => closeService.mutate({ id: r.id, status: 'CANCELLED' })}>
                        إلغاء
                      </Button>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {can(Perm.webOrderView) && (
        <Panel title="«حرف وتحدّي» الآن">
          <p className="mb-2 text-sm text-ink-soft">
            يتحدّث كلَّ ١٠ ثوانٍ. «متّصلون» = صفحةُ الألعاب مفتوحةٌ عندهم، في غرفةٍ أو تحدٍّ أو على الرئيسة.
          </p>
          <ErrorBanner error={live.error} />
          {live.isPending && <Spinner />}

          {live.data && (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <GameStat label="متّصلون الآن" value={live.data.connected} />
                <GameStat label="غرفٌ فعّالة" value={live.data.rooms} />
                <GameStat label="منها في مباراة" value={live.data.inMatch} />
                <GameStat label="لاعبون داخل الغرف" value={live.data.players} />
              </div>

              {live.data.rps && (
                <p className="mt-2 rounded-lg border border-line px-3 py-2 text-sm">
                  ✊✋✌️ <b>حجرة ورقة مقص:</b>{' '}
                  <span className="tabular">{live.data.rps.duels}</span> تحدّي
                  (<span className="tabular">{live.data.rps.inMatch}</span> بيلعبوا الآن،{' '}
                  <span className="tabular">{live.data.rps.vsComputer}</span> ضدّ الكمبيوتر) ·{' '}
                  <span className="tabular">{live.data.rps.players}</span> لاعب
                </p>
              )}

              {live.data.shayeb && (
                <p className="mt-2 rounded-lg border border-line px-3 py-2 text-sm">
                  👴🃏 <b>الشايب:</b>{' '}
                  <span className="tabular">{live.data.shayeb.tables}</span> طاولة
                  (<span className="tabular">{live.data.shayeb.inMatch}</span> بيلعبوا الآن) ·{' '}
                  <span className="tabular">{live.data.shayeb.players}</span> لاعب ·{' '}
                  <span className="tabular">{live.data.shayeb.bots}</span> كمبيوتر
                </p>
              )}

              {live.data.rooms === 0 && <Empty>لا غرفَ «حرف وتحدّي» الآن.</Empty>}

              <ul className="mt-3 grid gap-2">
                {live.data.list.map((room, i) => (
                  <li key={`${room.since}-${i}`} className="grid gap-2 rounded-lg border border-line px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className={`rounded px-2 ${room.public ? 'bg-ok/15 text-ok' : 'bg-brand/10 text-brand'}`}>
                        {room.public ? 'عامّة' : 'خاصّة'}
                      </span>
                      <b>{phaseName(room)}</b>
                      <span className="tabular text-ink-soft">
                        {room.players.filter((p) => p.online).length}/{room.maxPlayers}
                      </span>
                      <span className="tabular mr-auto text-xs text-ink-soft">{roomAge(room.since)}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {room.players.map((p) => (
                        <span
                          key={p.name}
                          className={`rounded-full border border-line px-2 py-0.5 text-sm ${p.online ? '' : 'opacity-50'}`}
                          title={p.online ? undefined : 'انقطع — مقعدُه محجوز'}
                        >
                          {p.avatar} {p.name}
                          {p.host && ' 👑'}
                          {p.guest && <span className="text-xs text-ink-soft"> ضيف</span>}
                          {room.phase !== 'lobby' && <span className="tabular text-xs text-ink-soft"> · {p.total}</span>}
                        </span>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
              {live.data.rooms > live.data.list.length && (
                <p className="mt-2 text-xs text-ink-soft">تظهر أوّلُ {live.data.list.length} غرفة من {live.data.rooms}.</p>
              )}
            </>
          )}
        </Panel>
      )}

      {can(Perm.webOrderView) && (
        <Panel title="كلماتُ «حرف وتحدّي» المقترحة">
          <p className="mb-2 text-sm text-ink-soft">
            كلماتٌ كتبها لاعبون على <a className="underline" href="https://aqabamobile.com/games" target="_blank" rel="noopener">aqabamobile.com/games</a>{' '}
            ولم يجدها القاموس. «اعتمد» تُدخلها القاموسَ فورًا — من الجولة التالية.
          </p>
          <ErrorBanner error={words.error ?? decideWord.error} />
          {words.data && words.data.length === 0 && <Empty>لا كلماتٍ بانتظار المراجعة.</Empty>}

          <ul className="grid gap-2">
            {words.data?.map((w) => (
              <li key={w.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-line px-3 py-2">
                <b className="text-lg">{w.word}</b>
                <span className="rounded bg-brand/10 px-2 text-sm text-brand">{categoryName[w.category]}</span>
                {w.letter && <span className="text-sm text-ink-soft">حرف {w.letter}</span>}
                <span className="text-xs text-ink-soft">{w.suggestedBy} · {formatMoment(w.createdAt)}</span>

                {handle && (
                  <span className="mr-auto flex gap-2">
                    <Button disabled={decideWord.isPending} onClick={() => decideWord.mutate({ id: w.id, approve: true })}>
                      اعتمد
                    </Button>
                    <Button tone="danger" disabled={decideWord.isPending} onClick={() => decideWord.mutate({ id: w.id, approve: false })}>
                      ارفض
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {can(Perm.webOrderView) && <OrderSettings />}
    </>
  )
}

function GameStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-line p-3">
      <div className="text-xs text-ink-soft">{label}</div>
      <div className="tabular mt-1 text-2xl font-bold">{value}</div>
    </div>
  )
}

function OrderCard({ order, canHandle, busy, onAct }: {
  order: WebOrder
  canHandle: boolean
  busy: boolean
  onAct: (action: string, staffNote?: string) => void
}) {
  const [note, setNote] = useState(order.staffNote ?? '')
  const open = order.status === 'NEW' || order.status === 'CONFIRMED'
  const total = order.deliveryFee === null ? null : order.itemsTotal + order.deliveryFee

  const hello = `مرحبا ${order.customerName}، معك عال العال للموبايل بخصوص طلبك ${order.orderNo}`

  return (
    <li className="rounded-xl border border-line p-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <b className="tabular" dir="ltr">{order.orderNo}</b>
        <span className={`rounded px-2 py-0.5 text-xs font-semibold ${statusTone[order.status]}`}>
          {statusName[order.status]}
        </span>
        <span className="font-semibold">{order.customerName}</span>
        <a className="tabular underline" dir="ltr" href={`tel:${order.phone}`}>{order.phone}</a>
        <span className="text-xs text-ink-soft">{formatMoment(order.createdAt)}</span>
      </div>

      <div className="mt-1 flex flex-wrap gap-2 text-sm">
        <span className="rounded bg-surface-raised px-2 py-0.5">
          {order.fulfilment === 'DELIVERY' ? 'توصيل' : 'استلامٌ من المحلّ'}
        </span>
        <span className="rounded bg-surface-raised px-2 py-0.5">
          {order.paymentMethod === 'CLIQ' ? 'CliQ' : 'الدفعُ عند الاستلام'}
          {order.paymentMethod === 'CLIQ' && order.cliqReference && (
            <> · مرجع <b dir="ltr">{order.cliqReference}</b></>
          )}
        </span>
        <span className={`rounded px-2 py-0.5 ${order.paid ? 'bg-ok/15 text-ok' : 'bg-surface-raised text-ink-soft'}`}>
          {order.paid ? 'مدفوع' : 'غيرُ مدفوعٍ بعد'}
        </span>
      </div>

      {order.address && <p className="mt-1 text-sm">📍 {order.address}</p>}
      {order.note && <p className="mt-1 text-sm text-ink-soft">ملاحظةُ الزبون: {order.note}</p>}

      <ul className="mt-2 grid gap-0.5 border-t border-line/60 pt-2 text-sm">
        {order.lines.map((line, i) => (
          <li key={i} className="flex items-baseline gap-2">
            <span className="tabular w-8 shrink-0 text-ink-soft" dir="ltr">×{line.qty}</span>
            <span className="min-w-0 flex-1">
              {line.productName}
              {line.variantLabel && <span className="text-ink-soft"> · {line.variantLabel}</span>}
            </span>
            <span className="tabular shrink-0" dir="ltr">{formatMoney(line.lineTotal)}</span>
          </li>
        ))}
      </ul>

      <div className="mt-2 flex flex-wrap justify-end gap-4 text-sm">
        {order.fulfilment === 'DELIVERY' && (
          <span>
            التوصيل:{' '}
            <b className="tabular" dir="ltr">
              {order.deliveryFee === null ? 'يُحدَّد' : formatMoney(order.deliveryFee)}
            </b>
          </span>
        )}
        <span>
          المجموع:{' '}
          <b className="tabular text-base" dir="ltr">
            {total === null ? `${formatMoney(order.itemsTotal)} + التوصيل` : formatMoney(total)}
          </b>
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <a
          className="rounded-md border border-line px-3 py-1.5 text-sm"
          href={whatsapp(order.phone, hello)}
          target="_blank"
          rel="noopener"
        >
          واتساب الزبون
        </a>

        {canHandle && open && (
          <>
            {order.status === 'NEW' && (
              <Button tone="primary" disabled={busy} onClick={() => onAct('CONFIRM')}>تأكيد</Button>
            )}
            {!order.paid && (
              <Button disabled={busy} onClick={() => onAct('MARK_PAID')}>
                {order.paymentMethod === 'CLIQ' ? 'وصل التحويل' : 'دُفع'}
              </Button>
            )}
            <Button disabled={busy} onClick={() => onAct('COMPLETE')}>سُلِّم — اكتمل</Button>
            <Button tone="danger" disabled={busy} onClick={() => onAct('CANCEL')}>إلغاء</Button>
          </>
        )}
      </div>

      {canHandle && (
        <div className="mt-2 flex gap-2">
          <div className="flex-1">
            <TextInput value={note} onChange={setNote} placeholder="ملاحظةٌ داخليّة (لا يراها الزبون)" />
          </div>
          {note !== (order.staffNote ?? '') && (
            <Button disabled={busy} onClick={() => onAct('NOTE', note)}>حفظ</Button>
          )}
        </div>
      )}
    </li>
  )
}

/**
 * CliQ والتوصيل — <b>تُضبط هنا وتظهر على الموقع فورًا، بلا نشرة</b>.
 *
 * ⚠ CliQ لا يُعرض للزبون حتى يُكتب الاسمُ المستعار: خيارُ دفعٍ بلا وجهةٍ
 *   يترك الزبونَ يسأل «أحوّل لمين؟».
 */
function OrderSettings() {
  const { can } = useAuth()
  const queries = useQueryClient()
  const [draft, setDraft] = useState<Options | null>(null)
  const [fee, setFee] = useState<string | null>(null)

  const current = useQuery({
    queryKey: ['settings', 'web-orders'],
    queryFn: () => api<Options>('/api/v1/settings/web-orders'),
  })

  const save = useMutation({
    mutationFn: (body: Options) => api<Options>('/api/v1/settings/web-orders', { method: 'PUT', body }),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: ['settings', 'web-orders'] })
      setDraft(null)
      setFee(null)
    },
  })

  if (!current.data) {
    return current.isPending ? <Spinner /> : <ErrorBanner error={current.error} />
  }

  const shown = draft ?? current.data
  const feeText = fee ?? (shown.deliveryFee === null ? '' : String(shown.deliveryFee))
  const parsedFee = feeText.trim() === '' ? null : toNumber(feeText)
  const set = (patch: Partial<Options>) => setDraft({ ...shown, ...patch })
  const editable = can(Perm.settingEdit)

  return (
    <Panel title="إعداداتُ الطلب من الموقع">
      <ErrorBanner error={save.error} />

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={shown.enabled} disabled={!editable}
            onChange={(e) => set({ enabled: e.target.checked })} />
          استقبالُ الطلبات من الموقع
        </label>

        <label className="flex items-center gap-2">
          <input type="checkbox" checked={shown.deliveryEnabled} disabled={!editable}
            onChange={(e) => set({ deliveryEnabled: e.target.checked })} />
          التوصيلُ متاح
        </label>

        <Field label="رسمُ التوصيل (دينار)" hint="فارغ = «يُحدَّد عند التأكيد». صفر = مجّاني.">
          <TextInput value={feeText} onChange={(v) => { setFee(v); setDraft(shown) }} placeholder="مثال: 2" />
        </Field>

        <Field label="منطقةُ التوصيل">
          <TextInput value={shown.deliveryArea ?? ''} onChange={(v) => set({ deliveryArea: v })} />
        </Field>

        <Field label="اسمُ CliQ المستعار" hint="فارغ = لا يظهر CliQ خيارًا للزبون.">
          <TextInput value={shown.cliqAlias ?? ''} onChange={(v) => set({ cliqAlias: v })} placeholder="ALIAS" />
        </Field>

        <Field label="اسمُ المستفيد كما يظهر في التحويل">
          <TextInput value={shown.cliqName ?? ''} onChange={(v) => set({ cliqName: v })} />
        </Field>
      </div>

      {editable && draft !== null && (
        <div className="mt-3">
          <Button
            tone="primary"
            disabled={save.isPending || (feeText.trim() !== '' && parsedFee === null)}
            onClick={() => save.mutate({ ...shown, deliveryFee: parsedFee })}
          >
            حفظ
          </Button>
        </div>
      )}
    </Panel>
  )
}
